import React from 'react';
import { motion } from 'framer-motion';
import { Star, Award, Clock, Brain } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import certificate1 from '../../assets/certificates/Hypnocoach.png';
import certificate2 from '../../assets/certificates/3.avif';
import certificate3 from '../../assets/certificates/Hypnose.gif';
import certificate4 from '../../assets/certificates/Logo-USA-removebg-preview.png';

export default function Trust() {
  const { t } = useTranslation();
  const stats = [
    {
      icon: <Star className="h-8 w-8" />,
      number: t('trust.rating'),
      label: t('trust.rating_label'),
      description: t('trust.rating_desc')
    },
    {
      icon: <Award className="h-8 w-8" />,
      number: t('trust.experience'),
      label: t('trust.experience_label'),
      description: t('trust.experience_desc')
    },
    {
      icon: <Clock className="h-8 w-8" />,
      number: t('trust.success'),
      label: t('trust.success_label'),
      description: t('trust.success_desc')
    },
    {
      icon: <Brain className="h-8 w-8" />,
      number: t('trust.methods'),
      label: t('trust.methods_label'),
      description: t('trust.methods_desc')
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.h2 variants={item} className="text-3xl md:text-4xl font-bold mb-4 text-dark-900">
            {t('trust.title')}
          </motion.h2>
          <motion.p variants={item} className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('trust.subtitle')}
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              variants={item}
              className="text-center group h-full"
            >
              <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:bg-gray-100 transition-all h-full flex flex-col">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <div className="text-rose-500">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-4xl font-bold text-dark-900 mb-2 group-hover:scale-110 transition-transform">
                  {stat.number}
                </div>
                <div className="text-lg font-semibold text-dark-900 mb-1">
                  {stat.label}
                </div>
                <div className="text-gray-600 text-sm flex-grow">
                  {stat.description}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          variants={item}
          className="mt-12 text-center"
        >
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 shadow-lg">
            <h3 className="text-xl sm:text-2xl font-semibold text-dark-900 mb-6 sm:mb-8">
              {t('trust.certified')}
            </h3>
            
            {/* Адаптивная сетка сертификатов */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Первый сертификат - полная ширина на мобильных */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-md p-4 sm:p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex flex-col sm:flex-row items-center justify-center h-32 sm:h-28 gap-3">
                  <img 
                    src={certificate3} 
                    alt="Сертификат Hypnose" 
                    className="h-16 sm:h-20 w-auto object-contain flex-shrink-0"
                  />
                  <img 
                    src={certificate1} 
                    alt="Сертификат Hypnocoach" 
                    className="h-16 sm:h-20 w-auto object-contain flex-shrink-0"
                  />
                </div>
              </div>
              
              {/* Второй сертификат */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-md p-4 sm:p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center justify-center h-32 sm:h-28">
                  <img 
                    src={certificate2} 
                    alt="Сертификат 3" 
                    className="h-20 sm:h-24 w-auto object-contain"
                  />
                </div>
              </div>
              
              {/* Третий сертификат */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-md p-4 sm:p-6 hover:shadow-lg transition-shadow duration-300 sm:col-span-2 lg:col-span-1">
                <div className="flex items-center justify-center h-32 sm:h-28">
                  <img 
                    src={certificate4} 
                    alt="Логотип USA" 
                    className="h-20 sm:h-24 w-auto object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Дополнительная информация */}
            <div className="mt-6 sm:mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {t('trust.certified_description')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
